import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import SingleCard from "../AdsPage/SingleCard";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
} from "../../Styles/Styles";
import LoadingAnimation from "../Animations/LoadingAnimation";
import { useNavigate } from "react-router-dom";
import CardSkeleton from "../AdsPage/CardSkeleton";

const RecentlyListedSlider = ({
  ads,
  isLoading,
  error,
  sectionTitle,
  sectionSubTitle,
  prevClass,
  nextClass,
  category,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Check if the device is mobile
  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 600); // Change this breakpoint if needed
  };

  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile); // Listen for window resize

    return () => {
      window.removeEventListener("resize", checkMobile); // Clean up listener on component unmount
    };
  }, []);

  const limitedAds = ads.slice(0, isMobile ? 3 : 10); // Limit to 3 on mobile, 10 on desktop

  const handleViewAll = () => {
    navigate("/ads");
  };

  return (
    <Box sx={{ margin: "auto", mt: 15, mb: 15, px: 2, width: "95%" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        {/* Left: Title Section */}
        <Box>
          <Typography
            variant="body1"
            sx={{ ...fonts, color: "text.secondary" }}
          >
            {sectionSubTitle}
          </Typography>
          <Typography
            variant="h5"
            sx={{ ...headingFonts, color: "text.primary" }}
          >
            {sectionTitle}
          </Typography>
        </Box>

        {/* Right: Navigation Arrows (only visible on desktop) */}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              className={prevClass}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--primary-color)",
                ...noOutline,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              className={nextClass}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--primary-color)",
                ...noOutline,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Loading State */}
      {/* {isLoading && <LoadingAnimation />} */}
      <Swiper
        spaceBetween={16} // gap between slides
        slidesPerView={5} // show 5 slides at once
      >
        {isLoading &&
          Array.from({ length: 5 }).map((_, index) => (
            <SwiperSlide key={index}>
              <CardSkeleton />
            </SwiperSlide>
          ))}
      </Swiper>

      {/* Error State */}
      {error && (
        <Typography
          variant="body2"
          sx={{ ...fonts, textAlign: "center", color: "red", my: 2 }}
        >
          {error}
        </Typography>
      )}

      {/* No Ads Found */}
      {!isLoading && !error && limitedAds.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            ...fonts,
            textAlign: "center",
            color: "text.secondary",
            my: 2,
          }}
        >
          No ads available.
        </Typography>
      )}

      {/* Swiper Component (Only Render if Ads Exist and Not Mobile) */}
      {!isLoading && !error && limitedAds.length > 0 && !isMobile && (
        <Swiper
          modules={[Navigation]}
          spaceBetween={25}
          slidesPerView={5}
          navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
          breakpoints={{
            1200: {
              slidesPerView: 5,
            },
            900: {
              slidesPerView: 4,
            },
            600: {
              slidesPerView: 3,
            },
            0: {
              slidesPerView: 1,
            },
          }}
        >
          {limitedAds.map((slide) => (
            <SwiperSlide key={slide.id}>
              <SingleCard AdInfo={slide} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Render Ads without Slider on Mobile */}
      {!isLoading && !error && limitedAds.length > 0 && isMobile && (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr" }}>
          {limitedAds.map((ad) => (
            <SingleCard key={ad.id} AdInfo={ad} />
          ))}
        </Box>
      )}
      {/* View All Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          className="main-button-style"
          sx={{ ...mainButtonStyles }}
          onClick={handleViewAll}
        >
          <span>View All</span>
          <ArrowForwardOutlinedIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default RecentlyListedSlider;
